import React, { FC, useCallback, useEffect, useState } from 'react';
import translate from '@translate';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import { Box, Typography } from '@mui/material';

import Table from 'src/components/Table';
import SimpleSwitch from 'src/components/SimpleSwitch';
import api from 'src/resources/api';
import { setLocationParams } from 'src/resources/location/reducer';
import { CustomRenderType, OnChangeOptionsType, TablePropsOptionsType } from 'src/types/table';
import { useCheckPermission } from 'src/utils/checkPermission';
import { useDebounce } from 'src/utils/debounce';
import { getDateTimeFormat } from 'src/utils/timeConstants';
import { colors } from 'src/themes/default';
import {
    allColumns,
    getProductTrashGridParams,
    ProductTrashGridResponse,
    ProductTrashRow,
    PRODUCT_TRASH_EVENT_TYPES,
    PRODUCT_TRASH_RESOLVED_COLUMN,
    PRODUCT_TRASH_SOURCE_VALUES,
} from './functions';

const GRID_URL = 'api/secure/products/trash/grid';
const SESSION_NAME = 'productTrashTable';

const initTableOptions: TablePropsOptionsType = {
    filters: [],
    sort: { column: 'CREATED_AT', direction: 'DESC' },
    page: 1,
    items: 20,
    columns: [],
};

const formatTrashDateTime = (iso: string | null | undefined): string => {
    if (!iso) {
        return '';
    }
    return moment(iso).format(getDateTimeFormat());
};

const translateEventType = (eventType: string): string => {
    const key = `productTrash.eventType.${eventType}`;
    const translated = translate(key);
    return translated === key ? eventType : translated;
};

const translateSource = (source: string): string => {
    const key = `productTrash.source.${source}`;
    const translated = translate(key);
    return translated === key ? source : translated;
};

const ProductTrashListContainer: FC = () => {
    const dispatch = useDispatch();
    const canList = useCheckPermission('PRODUCT.LIST');
    const sessionOptions = JSON.parse(sessionStorage.getItem(SESSION_NAME) || 'null') as TablePropsOptionsType | null;
    const [rows, setRows] = useState<ProductTrashRow[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [tableOptions, setTableOptions] = useState<TablePropsOptionsType>(sessionOptions || initTableOptions);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const debouncedTableOptions = useDebounce(tableOptions, 500);

    const loadTableData = useCallback(async (options: TablePropsOptionsType) => {
        if (!canList || !options.columns.length) {
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const res = await api('post', GRID_URL, getProductTrashGridParams(options));
            const payload = res.data?.data as ProductTrashGridResponse | undefined;
            setRows(payload?.data ?? []);
            setTotalCount(payload?.totalCount ?? 0);
            await api('post', 'api/secure/products/trash/mark-seen', {});
        } catch {
            setError(translate('productTrash.loadError'));
        } finally {
            setLoading(false);
        }
    }, [canList]);

    useEffect(() => {
        dispatch(setLocationParams({ name: translate('productTrash.title') }));
    }, [dispatch]);

    useEffect(() => {
        void loadTableData(debouncedTableOptions);
    }, [debouncedTableOptions, loadTableData]);

    const onResolveChange = async (id: number, resolved: boolean) => {
        try {
            await api('post', `api/secure/products/trash/${id}/${resolved ? 'resolve' : 'unresolve'}`, {});
            await loadTableData(tableOptions);
        } catch {
            setError(translate('productTrash.resolveError'));
        }
    };

    const handleChangeOptions: OnChangeOptionsType = (newOptions) => {
        setTableOptions(newOptions);
    };

    const customRender: CustomRenderType = (column, row) => {
        if (column.key === 'CREATED_AT') {
            return formatTrashDateTime(row.CREATED_AT as string | null | undefined);
        }

        if (column.key === 'EVENT_TYPE') {
            return translateEventType(String(row.EVENT_TYPE || ''));
        }

        if (column.key === 'SOURCE') {
            return translateSource(String(row.SOURCE || ''));
        }

        if (column.key === PRODUCT_TRASH_RESOLVED_COLUMN) {
            const resolved = !!row.RESOLVED_AT;
            const rowId = Number(row.ID);

            return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SimpleSwitch
                        value={resolved}
                        onChange={(checked) => {
                            if (Number.isFinite(rowId)) {
                                void onResolveChange(rowId, checked);
                            }
                        }}
                    />
                    <span>{translate(resolved ? 'utils.boolCodes.yes' : 'utils.boolCodes.no')}</span>
                </Box>
            );
        }

        return String(row[column.key] ?? '');
    };

    if (!canList) {
        return (
            <Box p={2}>
                <Typography>{translate('productTrash.noPermission')}</Typography>
            </Box>
        );
    }

    return (
        <>
            {error && (
                <Typography color="error" sx={{ mb: 1 }}>
                    {error}
                </Typography>
            )}
            <Table
                allColumns={allColumns}
                rowIdKey="ID"
                rows={rows}
                loading={loading}
                columsLabelPrefix="productTrash.column"
                options={tableOptions}
                totalCount={totalCount}
                onChangeOptions={handleChangeOptions}
                customRender={customRender}
                selectOptions={{
                    productTrashEventTypeCodes: PRODUCT_TRASH_EVENT_TYPES.map((eventType) => ({
                        label: translateEventType(eventType),
                        value: eventType,
                    })),
                    productTrashSourceCodes: PRODUCT_TRASH_SOURCE_VALUES.map((source) => ({
                        label: translateSource(source),
                        value: source,
                    })),
                    productTrashResolvedCodes: [
                        { label: translate('utils.boolCodes.yes'), value: 1 },
                        { label: translate('utils.boolCodes.no'), value: 0 },
                    ],
                }}
                rowSxFce={(row) => {
                    if (row.RESOLVED_AT) {
                        return { color: colors.text_T3, '& .MuiTableCell-root': { color: colors.text_T3 } };
                    }
                    return {};
                }}
                heightVariant="top-tab"
                fullHeight
                optionsSessionName={SESSION_NAME}
            />
        </>
    );
};

export default ProductTrashListContainer;
